import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Building2, FileText, PackagePlus, CheckCircle2, Clock } from "lucide-react";
import api from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { fmtTRY, fmtDate, fmtQty4  } from "../lib/format";
import { errText } from "../lib/errors";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { toast } from "sonner";
import { trackEvent } from "../lib/analytics";

const inputCls = "w-full h-11 px-4 rounded-xl border border-stone-300 text-sm focus:outline-none focus:ring-2 focus:ring-forest/30";

export default function B2B() {
  const { user, loading, refresh } = useAuth();
  const [company, setCompany] = useState(undefined);
  const [form, setForm] = useState({ company_name: "", tax_office: "", tax_number: "", contact_person: "", phone: "", address: "", city: "", monthly_volume: "" });
  const [rfqs, setRfqs] = useState([]);
  const [products, setProducts] = useState([]);
  const [rfqForm, setRfqForm] = useState({ product_id: "", quantity: 10, target_price: "", note: "", delivery_date: "" });
  const [bulkText, setBulkText] = useState("ATA001, 20\nATA006, 30");
  const [bulkResult, setBulkResult] = useState(null);

  useEffect(() => {
    if (!user) return;
    api.get("/b2b/profile").then(({ data }) => setCompany(data.company)).catch(() => setCompany(null));
    api.get("/products?limit=100").then(({ data }) => setProducts(data.items)).catch(() => {});
  }, [user]);

  useEffect(() => {
    if (company?.status === "approved") api.get("/b2b/rfqs").then(({ data }) => setRfqs(data)).catch(() => {});
  }, [company]);

  if (loading) return null;
  if (!user) {
    return (
      <div className="max-w-xl mx-auto px-4 py-24 text-center">
        <Building2 className="w-12 h-12 text-gold mx-auto" />
        <h1 className="font-serif text-3xl text-forest mt-4">B2B Toptan Satış</h1>
        <p className="text-stone-500 mt-3">Kurumsal başvuru için önce giriş yapmalısınız.</p>
        <Link to="/giris" data-testid="b2b-login-link" className="inline-block mt-6 bg-forest hover:bg-forest-dark text-white font-semibold px-7 py-3 rounded-xl transition-colors">Giriş Yap / Üye Ol</Link>
      </div>
    );
  }

  const apply = async (e) => {
    e.preventDefault();
    try {
      const { data } = await api.post("/b2b/apply", form);
      trackEvent("b2b_application", { company: form.company || form.company_name || "" });
      setCompany(data);
      await refresh();
      toast.success("Başvurunuz alındı. Onay sonrası özel fiyatlar aktifleşecek.");
    } catch (err) { toast.error(errText(err) || "Başvuru gönderilemedi"); }
  };

  const submitRfq = async (e) => {
    e.preventDefault();
    try {
      await api.post("/b2b/rfq", { items: [{ product_id: rfqForm.product_id, quantity: Number(rfqForm.quantity), target_price: rfqForm.target_price ? Number(rfqForm.target_price) : null }], note: rfqForm.note, delivery_date: rfqForm.delivery_date });
      toast.success("Teklif talebiniz oluşturuldu");
      const { data } = await api.get("/b2b/rfqs");
      setRfqs(data);
    } catch (err) { toast.error(errText(err) || "Talep oluşturulamadı"); }
  };

  const previewBulk = async () => {
    const items = bulkText.split("\n").map((l) => l.trim()).filter(Boolean).map((l) => {
      const [sku, qty] = l.split(/[,;\t]/).map((s) => s.trim());
      return { sku, quantity: Number(qty) || 0 };
    });
    try {
      const { data } = await api.post("/b2b/bulk-order", { items });
      setBulkResult(data);
    } catch (err) { toast.error(errText(err) || "Önizleme alınamadı"); }
  };

  const acceptQuote = async (rfqId) => {
    try {
      await api.post(`/b2b/rfqs/${rfqId}/accept`);
      toast.success("Teklif kabul edildi");
      const { data } = await api.get("/b2b/rfqs");
      setRfqs(data);
    } catch (err) { toast.error(errText(err) || "İşlem başarısız"); }
  };

  return (
    <div data-testid="b2b-page" className="max-w-5xl mx-auto px-4 py-10">
      <div className="flex items-center gap-4">
        <Building2 className="w-10 h-10 text-gold" />
        <div>
          <h1 className="font-serif text-3xl sm:text-4xl text-forest">B2B Toptan Satış Portalı</h1>
          <p className="text-stone-500 text-sm mt-1">Özel fiyatlar, iskontolar ve toplu sipariş avantajları</p>
        </div>
      </div>

      {company === undefined ? <p className="mt-10 text-stone-500">Yükleniyor...</p> : !company ? (
        <form onSubmit={apply} className="mt-8 bg-white rounded-2xl border border-stone-200/80 p-6 sm:p-8">
          <h2 className="font-semibold text-lg text-stone-900">Kurumsal Başvuru Formu</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mt-5">
            <input data-testid="b2b-company-name" required className={inputCls} placeholder="Firma Adı *" value={form.company_name} onChange={(e) => setForm({ ...form, company_name: e.target.value })} />
            <input data-testid="b2b-contact-person" required className={inputCls} placeholder="Yetkili Kişi *" value={form.contact_person} onChange={(e) => setForm({ ...form, contact_person: e.target.value })} />
            <input data-testid="b2b-tax-office" required className={inputCls} placeholder="Vergi Dairesi *" value={form.tax_office} onChange={(e) => setForm({ ...form, tax_office: e.target.value })} />
            <input data-testid="b2b-tax-number" required className={inputCls} placeholder="Vergi Numarası *" value={form.tax_number} onChange={(e) => setForm({ ...form, tax_number: e.target.value })} />
            <input data-testid="b2b-phone" required className={inputCls} placeholder="Telefon *" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} />
            <input data-testid="b2b-city" required className={inputCls} placeholder="İl *" value={form.city} onChange={(e) => setForm({ ...form, city: e.target.value })} />
            <input data-testid="b2b-volume" className={inputCls} placeholder="Aylık tahmini hacim (ör. 50.000 TL)" value={form.monthly_volume} onChange={(e) => setForm({ ...form, monthly_volume: e.target.value })} />
            <textarea data-testid="b2b-address" required className={`${inputCls} h-auto py-3`} rows={2} placeholder="Adres *" value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} />
          </div>
          <button data-testid="b2b-apply-button" className="mt-6 bg-forest hover:bg-forest-dark text-white font-semibold px-8 py-3.5 rounded-xl transition-colors">Başvuruyu Gönder</button>
        </form>
      ) : company.status === "pending" ? (
        <div className="mt-8 bg-amber-50 border border-amber-200 rounded-2xl p-8 text-center">
          <Clock className="w-10 h-10 text-amber-600 mx-auto" />
          <h2 className="font-serif text-2xl text-stone-900 mt-4">Başvurunuz İnceleniyor</h2>
          <p className="text-stone-600 text-sm mt-2">{company.company_name} — başvurunuz satış ekibimiz tarafından değerlendiriliyor. Onaylandığında özel fiyatlarınız aktifleşecek.</p>
        </div>
      ) : company.status === "rejected" ? (
        <div className="mt-8 bg-red-50 border border-red-200 rounded-2xl p-8 text-center">
          <h2 className="font-serif text-2xl text-stone-900">Başvurunuz Onaylanmadı</h2>
          <p className="text-stone-600 text-sm mt-2">Detaylı bilgi için bizimle iletişime geçin.</p>
        </div>
      ) : (
        <>
          <div className="mt-8 bg-forest text-cream rounded-2xl p-6 flex flex-wrap items-center justify-between gap-4">
            <div>
              <p className="font-semibold text-lg">{company.company_name}</p>
              <p className="text-cream/70 text-sm mt-1 flex items-center gap-2"><CheckCircle2 className="w-4 h-4 text-gold" /> Onaylı B2B Hesap · %{company.discount_rate} iskonto</p>
            </div>
            <p className="text-sm text-cream/80">Tüm fiyatlarınıza %{company.discount_rate} B2B iskontosu otomatik uygulanır.</p>
          </div>

          <Tabs defaultValue="rfq" className="mt-8">
            <TabsList className="bg-cream-dark/70 flex-wrap h-auto gap-1 p-1.5 rounded-xl">
              <TabsTrigger data-testid="b2b-tab-rfq" value="rfq" className="rounded-lg"><FileText className="w-4 h-4 mr-1.5" /> Teklif Talebi</TabsTrigger>
              <TabsTrigger data-testid="b2b-tab-bulk" value="bulk" className="rounded-lg"><PackagePlus className="w-4 h-4 mr-1.5" /> Toplu Sipariş</TabsTrigger>
              <TabsTrigger data-testid="b2b-tab-my-rfqs" value="myrfqs" className="rounded-lg">Tekliflerim ({rfqs.length})</TabsTrigger>
            </TabsList>

            <TabsContent value="rfq" className="mt-6 bg-white rounded-2xl border border-stone-200/80 p-6">
              <h3 className="font-semibold text-stone-900">Teklif Talebi Oluştur</h3>
              <form onSubmit={submitRfq} className="grid grid-cols-1 sm:grid-cols-2 gap-4 mt-4">
                <select data-testid="rfq-product" required value={rfqForm.product_id} onChange={(e) => setRfqForm({ ...rfqForm, product_id: e.target.value })} className={inputCls}>
                  <option value="">Ürün seçin *</option>
                  {products.map((p) => <option key={p.product_id} value={p.product_id}>{p.sku} — {p.name}</option>)}
                </select>
                <input data-testid="rfq-quantity" type="number" min={1} required className={inputCls} placeholder="Miktar *" value={rfqForm.quantity} onChange={(e) => setRfqForm({ ...rfqForm, quantity: e.target.value })} />
                <input data-testid="rfq-target-price" type="number" step="0.01" className={inputCls} placeholder="Hedef birim fiyat (TL, opsiyonel)" value={rfqForm.target_price} onChange={(e) => setRfqForm({ ...rfqForm, target_price: e.target.value })} />
                <input data-testid="rfq-delivery-date" type="text" className={inputCls} placeholder="Talep edilen teslimat (ör. 2 hafta)" value={rfqForm.delivery_date} onChange={(e) => setRfqForm({ ...rfqForm, delivery_date: e.target.value })} />
                <textarea data-testid="rfq-note" className={`${inputCls} h-auto py-3 sm:col-span-2`} rows={2} placeholder="Açıklama" value={rfqForm.note} onChange={(e) => setRfqForm({ ...rfqForm, note: e.target.value })} />
                <button data-testid="rfq-submit" className="bg-forest hover:bg-forest-dark text-white font-semibold px-8 py-3 rounded-xl transition-colors sm:col-span-2 sm:w-fit">Talebi Gönder</button>
              </form>
            </TabsContent>

            <TabsContent value="bulk" className="mt-6 bg-white rounded-2xl border border-stone-200/80 p-6">
              <h3 className="font-semibold text-stone-900">SKU ile Toplu Sipariş</h3>
              <p className="text-sm text-stone-500 mt-1">Her satıra "SKU, adet" yazın. Örnek: <code className="bg-cream-dark px-1.5 py-0.5 rounded">ATA001, 20</code></p>
              <textarea data-testid="bulk-order-input" rows={5} className="w-full mt-4 px-4 py-3 rounded-xl border border-stone-300 text-sm font-mono focus:outline-none focus:ring-2 focus:ring-forest/30" value={bulkText} onChange={(e) => setBulkText(e.target.value)} />
              <button data-testid="bulk-preview-button" onClick={previewBulk} className="mt-4 bg-forest hover:bg-forest-dark text-white font-semibold px-8 py-3 rounded-xl transition-colors">Fiyat Önizlemesi Al</button>
              {bulkResult && (
                <div data-testid="bulk-result" className="mt-6">
                  {bulkResult.errors.map((e) => <p key={e.sku} className="text-sm text-terra">⚠ {e.sku}: {e.error}</p>)}
                  <table className="w-full text-sm mt-3">
                    <thead><tr className="text-left text-stone-500 border-b border-stone-200"><th className="py-2">SKU</th><th>Ürün</th><th>Adet</th><th>Birim (B2B)</th><th className="text-right">Tutar</th></tr></thead>
                    <tbody>
                      {bulkResult.items.map((i) => (
                        <tr key={i.sku} className="border-b border-stone-100"><td className="py-2 font-mono">{i.sku}</td><td>{i.name}</td><td>{fmtQty4(i.quantity, i.unit || "")}</td><td>{fmtTRY(i.unit_price)}</td><td className="text-right font-semibold">{fmtTRY(i.total)}</td></tr>
                      ))}
                    </tbody>
                    <tfoot><tr><td colSpan={4} className="py-3 font-bold">Genel Toplam (%{bulkResult.discount_rate} iskontolu)</td><td className="text-right font-bold text-forest text-lg">{fmtTRY(bulkResult.total)}</td></tr></tfoot>
                  </table>
                  <p className="text-xs text-stone-500 mt-3">Bu tutarları onaylamak için siparişinizi WhatsApp veya satış temsilciniz üzerinden tamamlayabilirsiniz.</p>
                </div>
              )}
            </TabsContent>

            <TabsContent value="myrfqs" className="mt-6 space-y-4">
              {rfqs.length === 0 && <p className="text-sm text-stone-500 bg-white rounded-2xl border border-stone-200/80 p-8 text-center">Henüz teklif talebiniz yok.</p>}
              {rfqs.map((r) => (
                <div key={r.rfq_id} data-testid={`rfq-card-${r.rfq_id}`} className="bg-white rounded-2xl border border-stone-200/80 p-5">
                  <div className="flex flex-wrap justify-between gap-2">
                    <div>
                      <p className="font-mono text-sm font-bold text-stone-900">{r.rfq_id}</p>
                      <p className="text-xs text-stone-500">{fmtDate(r.created_at)} · {r.items.length} kalem</p>
                    </div>
                    <span className={`text-xs font-semibold px-3 py-1.5 rounded-full h-fit ${r.status === "quoted" ? "bg-green-100 text-green-800" : r.status === "accepted" ? "bg-forest text-white" : "bg-amber-100 text-amber-800"}`}>
                      {r.status === "pending" ? "Değerlendiriliyor" : r.status === "quoted" ? "Teklif Hazır" : "Kabul Edildi"}
                    </span>
                  </div>
                  <div className="mt-3 text-sm space-y-1">
                    {r.items.map((i) => (
                      <div key={i.product_id} className="flex justify-between">
                        <span className="text-stone-700">{i.name} × {fmtQty4(i.quantity, i.unit || "")}</span>
                        <span className="font-medium">{i.quoted_price ? fmtTRY(i.quoted_price * i.quantity) : "—"}</span>
                      </div>
                    ))}
                  </div>
                  {r.status === "quoted" && (
                    <div className="mt-4 flex flex-wrap items-center justify-between gap-3 border-t border-stone-100 pt-4">
                      <p className="font-bold text-forest">Teklif Toplamı: {fmtTRY(r.quoted_total)}</p>
                      <button data-testid={`rfq-accept-${r.rfq_id}`} onClick={() => acceptQuote(r.rfq_id)} className="bg-forest hover:bg-forest-dark text-white text-sm font-semibold px-6 py-2.5 rounded-xl transition-colors">Teklifi Kabul Et</button>
                    </div>
                  )}
                  {r.quote_note && <p className="text-xs text-stone-500 mt-2">Not: {r.quote_note}</p>}
                </div>
              ))}
            </TabsContent>
          </Tabs>
        </>
      )}
    </div>
  );
}
